import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { SessionInfo } from "../models/SessionInfo";
import { AuthorizedUser } from "../models/AuthorizedUser";

enum StatusCode {
    Unauthorized = 401,
    Forbidden = 403,
    TooManyRequests = 429,
    InternalServerError = 500,
}

const headers: Readonly<Record<string, string | boolean>> = {
    "Content-Type": "application/json",
    "Accept": "application/json",
    // "Access-Control-Allow-Origin": "*",
    // "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
    // "Access-Control-Allow-Credentials": true,
    // "X-Requested-With": "XMLHttpRequest",
};

const injectToken = (config: AxiosRequestConfig): AxiosRequestConfig => {
    //try {
    let authorizedUserToken = SessionInfo.IsLogitudeAuthentication ?
        SessionInfo.AuthorizedUserToken :
        AuthorizedUser.getToken();

    if (authorizedUserToken !== null) {
        config.headers.Authorization = `Bearer ${authorizedUserToken}`;
    }
    //if (token != null) {

    //}
    return config;
    //} catch (error) {
    //throw new Error(error);
    //}
};

const injectLogitudeTokens = (config: AxiosRequestConfig): AxiosRequestConfig => {

    //let logitudeTokens = localStorage.getItem("LogitudeTokens");

    let logitudeTokens = SessionInfo.IsLogitudeAuthentication ?
        SessionInfo.LogitudeTokensJson :
        localStorage.getItem("LogitudeTokens");

    if (logitudeTokens !== null) {
        config.headers.LogitudeTokens = logitudeTokens;
    }

    return config;
};

const injectApplicationPathNameHeader = (config: AxiosRequestConfig): AxiosRequestConfig => {
    config.headers.ApplicationPathName = window.location.pathname.toLowerCase();
    return config;
};

class Http {

    private baseUrl: string = this.getApiEndPointUrl().replaceAll(" ", "") + "/api";

    private instance: AxiosInstance | null = null;

    private get http(): AxiosInstance {
        if (this.instance != null) {
            this.instance.interceptors.request.use(injectApplicationPathNameHeader, (error) => Promise.reject(error));
            //this.instance.interceptors.request.use(injectLogitudeTokens, (error) => Promise.reject(error));
        }
        return this.instance != null ? this.instance : this.initHttp();
    }

    initHttp() {
        const http = axios.create({
            baseURL: this.baseUrl,
            headers//,
            //withCredentials: true,
        });

        http.interceptors.request.use(injectToken, (error) => Promise.reject(error));
        http.interceptors.request.use(injectApplicationPathNameHeader, (error) => Promise.reject(error));
        http.interceptors.request.use(injectLogitudeTokens, (error) => Promise.reject(error));

        http.interceptors.response.use(
            (response) => {
                // let errorMessages = response.data.ErrorMessages;
                // if (errorMessages && errorMessages.indexOf("NotAuthorized") !== -1) {
                //     AuthorizedUser.resetAuthorizedUser();
                //     window.location.href = "/";
                // }
                return response;
            },
            (error) => {
                const { response } = error;
                return this.handleError(response);
            }
        );

        this.instance = http;
        return http;
    }

    getApiEndPointUrl() {
        let localhostEndPointUrl = "http://localhost:40463";
        let currentHost = window.location.host.toLowerCase();
        let urlPrefix = this.getUrlPrefix();

        if (currentHost.includes("localhost")) {
            // if (process) {
            //     return process.env.REACT_APP_API_ENDPOINT || localhostEndPointUrl;
            // }
            return window?.globalThis["CToolLocallyApiEndPointUrl"] || localhostEndPointUrl;
        }

        switch (currentHost) {
            case "system.logitudeworld.com":
            case "systemnew.logitudeworld.com":
            case "app.unicargo.com":
            case "test.unicargo.com":
                return urlPrefix + "app.unicargo.com/api";

            case "test.logitudeworld.com":
            case "testnew.logitudeworld.com":
                return urlPrefix + "ctool.logitudeworld.com/api";

            case "staging.unicargo.com":
                return urlPrefix + "staging.unicargo.com/api";

            case "testwebnew.westeurope.cloudapp.azure.com":
                return urlPrefix + "testwebnew.westeurope.cloudapp.azure.com/api";

            case "testwebnew.westeurope.cloudapp.azure.com:81":
                return urlPrefix + "testwebnew.westeurope.cloudapp.azure.com:81/api";

            case "app.iclusa.us":
            case "test.iclusa.us":
                return urlPrefix + "app.iclusa.us/api";

            default:
                return localhostEndPointUrl;
        }
    }

    getUrlPrefix() {
        let currentUrl = window.location.href.toLowerCase();
        if (currentUrl.indexOf("https") === 0) {
            return "https://";
        }
        return "http://";
    }

    request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
        return this.http.request(config);
    }

    getFullUrl(url: string): string {
        this.setBaseUrl(url);
        return this.baseUrl + "/" + url;
    }

    get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        this.setBaseUrl(url);
        return this.http.get<T, R>(url, config);
    }

    post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        this.setBaseUrl(url);
        return this.http.post<T, R>(url, data, config);
    }

    put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        this.setBaseUrl(url);
        return this.http.put<T, R>(url, data, config);
    }

    delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        this.setBaseUrl(url);
        return this.http.delete<T, R>(url, config);
    }

    private setBaseUrl(url: string) {
        if (this.isUrlAbsolute(url)) {
            this.http.defaults.baseURL = url;
        } else {
            this.http.defaults.baseURL = this.baseUrl;
        }
    }

    private isUrlAbsolute(url: string) {
        return url.indexOf('//') === 0 ? true :
            url.indexOf('://') === -1 ? false :
                url.indexOf('.') === -1 ? false :
                    url.indexOf('/') === -1 ? false :
                        url.indexOf(':') > url.indexOf('/') ? false :
                            url.indexOf('://') < url.indexOf('.') ? true : false;
    }

    private handleError(error: any) {
        const { status } = error;

        switch (status) {
            case StatusCode.InternalServerError: {
                // Handle InternalServerError
                console.log("InternalServerError:" + error);
                break;
            }
            case StatusCode.Forbidden: {
                // Handle Forbidden
                console.log("Forbidden:" + error);
                break;
            }
            case StatusCode.Unauthorized: {
                console.log("Unauthorized:" + error);
                break;
            }
            case StatusCode.TooManyRequests: {
                // Handle TooManyRequests
                console.log("TooManyRequests:" + error);
                break;
            }
        }

        return Promise.reject(error);
    }
}

export const http = new Http();